import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Identity,
  IdentityService,
  TOKENS,
} from '@trustedshops/tswp-core-authorization';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  from,
  of,
  Subject,
  Observable,
  EMPTY,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { KeycloakProfileData } from '@trustedshops/tswp-core-authorization-keycloak';
import { RxJsSubjectBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';
import {
  CultureInfo,
  CultureInfoService,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  OrganizationalContainer,
  ProductFeatureService,
  TOKENS as MASTERDATA_TOKENS,
  UserFlowService,
} from '@trustedshops/tswp-core-masterdata';
import { environment } from '../configuration/get-environment.util';
import { AccountService } from './account.service';
import { UserFlowIdentity } from '../models/user-flow-identity.interface';
import { UserFlowAssociationService } from './user-flow-association.service';
import { ChannelsService } from './channels.service';
import { UserFlowProgressService } from './user-flow-progress.service';
import { OnboardingProgress } from '../models/onboarding-progress.interface';
import { UserFlowPermissionsService } from './user-flow-permissions.service';
import { CompanyService } from './company.service';
import { Company } from '../models/company.interface';

// Define interfaces for better type safety
interface UserProfile {
  identityRef: string;
  email: string;
  given_name: string;
  family_name: string;
  'https://etrusted.com'?: { accountRef: string };
}

interface UserMetadata {
  hasFreeOnboardingFeature: boolean;
  hasPaidOnboardingFeature: boolean;
  profile: UserProfile;
  accountId: string;
  channelId: string;
  accountCreatedAt?: string;
  locale_code?: string;
  createdAt?: string;
  company: Company;
}

@Injectable()
export class UserOnboardingInitService {
  private static readonly TYPE = 'UserFlowService';
  private static readonly PROGRESS_COMPLETE = 100;
  private static readonly FEAT_ONBOARDING_FREE = 'FEAT_ONBOARDING_FREE';
  private static readonly FEAT_ONBOARDING_PAID = 'FEAT_ONBOARDING_PAID';

  // Public observables
  public readonly shouldShowOnboarding$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public readonly progress$ = new BehaviorSubject<number>(0);
  public readonly userMetadata$ = new BehaviorSubject<UserMetadata | undefined>(
    undefined
  );

  // Public properties
  public checklistIds: string[] = [];
  public ongoingOnboardingId?: string;

  constructor(
    @Inject(TOKENS.IdentityService)
    private readonly identityService: IdentityService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly productFeatureService: ProductFeatureService,
    @Inject(COMMON_TOKENS.CultureInfoService)
    private readonly cultureInfoService: CultureInfoService,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly channelsService: ChannelsService,
    private readonly userFlowAssociationService: UserFlowAssociationService,
    private readonly userFlowPermissionsService: UserFlowPermissionsService,
    private readonly userFlowProgressService: UserFlowProgressService,
    private readonly companyService: CompanyService,

    @Inject(MASTERDATA_TOKENS.UserFlowService)
    private readonly userFlowService: UserFlowService
  ) {}

  /**
   * Initializes the UserFlow library and updates user profile
   */
  public init(): void {
    // Initialize UserFlow
    this.initializeUserFlow$()
      .pipe(
        take(1),
        catchError((error) => {
          this.logError('UserFlow initialization failed', error);

          return EMPTY;
        })
      )
      .subscribe();

    // Initialize Onboarding visibility check
    combineLatest([this.userFlowService.isReady$, this.userMetadata$])
      .pipe(
        filter(([isReady, userMetadata]) => isReady && !!userMetadata),
        map(([, userMetadata]) => this.shouldShowOnboarding(userMetadata)),
        tap((shouldShowOnboarding) =>
          this.shouldShowOnboarding$.next(shouldShowOnboarding)
        ),
        filter((shouldShowOnboarding) => shouldShowOnboarding),
        switchMap(() => this.loadProgress()),
        take(1),
        catchError((error) => {
          this.logError('Onboarding initialization failed', error);

          return EMPTY;
        })
      )
      .subscribe();

    // Init permissions and channel association
    this.setupChannelAssociation();
    this.updateUserPermissionsInUserflow();
  }

  /**
   * Loads user onboarding progress
   */
  public loadProgress(): Observable<OnboardingProgress | null> {
    const userId = this.userMetadata$.getValue()?.profile?.identityRef;

    if (!userId) {
      return of(null);
    }

    return this.userFlowProgressService
      .getFirstIncompleteOnboarding(this.checklistIds, userId)
      .pipe(tap((result) => this.updateProgressState(result)));
  }

  /**
   * Main initialization flow for UserFlow
   */
  private initializeUserFlow$(): Observable<void> {
    return this.getIdentityProfile$().pipe(
      // Collect data needed to check if UserFlow should be initialized
      switchMap((profile) => this.collectData(profile)),
      // Set checklist IDs based on account state
      tap((userMetadata) => this.setChecklistIds(userMetadata)),
      // Initialize UserFlow with the identity
      switchMap((userMetadata) => from(this.setupUserFlow(userMetadata)))
    );
  }

  /**
   * Gets the identity profile from the identity service
   */
  private getIdentityProfile$(): Observable<UserProfile> {
    return this.identityService.identity
      .convertWith<Subject<Identity<KeycloakProfileData>>>(RxJsSubjectBridge)
      .pipe(
        filter(
          (identity): identity is Identity<KeycloakProfileData> => !!identity
        ),
        first(),
        map((identity) => identity.profile.keycloak.token.payload),
        switchMap((payload) =>
          payload.convertWith<Subject<UserProfile>>(RxJsSubjectBridge)
        ),
        shareReplay(1)
      );
  }

  /**
   * Gets the complete account state including PFM feature flags
   */
  private collectData(profile: UserProfile): Observable<UserMetadata> {
    const accountId = profile['https://etrusted.com']?.accountRef || '';

    return forkJoin({
      hasFreeOnboardingFeature: from(
        this.productFeatureService.hasFeature({
          featureNames: UserOnboardingInitService.FEAT_ONBOARDING_FREE,
        })
      ),
      hasPaidOnboardingFeature: from(
        this.productFeatureService.hasFeature({
          featureNames: UserOnboardingInitService.FEAT_ONBOARDING_PAID,
        })
      ),
      profile: of(profile),
      accountId: of(accountId),
      channelId: this.channelsService.selectedChannelId$.pipe(take(1)),
      company: this.companyService.company$.pipe(take(1)),
    }).pipe(
      switchMap((userMetadata) =>
        this.enrichWithAccountCreatedAt$(userMetadata)
      ),
      switchMap((userMetadata) =>
        this.enrichWithLocaleAndCreatedAt$(userMetadata)
      )
    );
  }

  /**
   * Enriches account state with creation date
   */
  private enrichWithAccountCreatedAt$(
    userMetadata: UserMetadata
  ): Observable<UserMetadata> {
    if (!userMetadata.accountId) {
      return of(userMetadata);
    }

    return this.accountService
      .getAccountCreatedAtDate(userMetadata.accountId)
      .pipe(
        map(({ createdAt }) => ({
          ...userMetadata,
          accountCreatedAt: createdAt,
        })),
        catchError((error) => {
          this.logError('Failed to get account creation date', error);
          return of(userMetadata);
        })
      );
  }

  /**
   * Enriches state with locale and user creation date
   */
  private enrichWithLocaleAndCreatedAt$(
    userMetadata: UserMetadata
  ): Observable<UserMetadata> {
    return combineLatest([
      this.getLocaleCode$(),
      this.getUserCreatedDate$(),
    ]).pipe(
      map(([locale_code, createdAt]) => ({
        ...userMetadata,
        locale_code,
        createdAt,
      }))
    );
  }

  /**
   * Gets the current locale code
   */
  private getLocaleCode$(): Observable<string> {
    return this.cultureInfoService.currentCulture
      .convertWith<Observable<CultureInfo>>(RxJsSubjectBridge)
      .pipe(
        map((cultureInfo) => cultureInfo.ietfLanguageTag),
        distinctUntilChanged(),
        catchError(() => {
          this.logError('Failed to get locale code');
          return of('en-GB');
        })
      );
  }

  /**
   * Gets the user creation date from the session token
   */
  private getUserCreatedDate$(): Observable<string> {
    return this.identityService.sessionProvider.parsedToken.pipe(
      filter((payload) => !!payload),
      map(({ kcUserCreated }) => new Date(kcUserCreated).toISOString()),
      first()
    );
  }

  /**
   * Determines if UserFlow should be initialized for this account.
   * Unverified/reduced-nav accounts won't have either onboarding feature, so
   * they are naturally excluded without an explicit isUnverifiedAccount check.
   *
   * TEMP WORKAROUND (CC-1147 / PSUP-5378): paid accounts also carry FEAT_ONBOARDING_FREE
   * (via PROD_BASIC_ACCESS), so paid takes priority — an account with the paid feature must
   * never fall back to the free onboarding flow. Free onboarding therefore only applies to
   * accounts that do NOT carry the paid feature. Revert to the plain OR once the backend
   * stops sending FEAT_ONBOARDING_FREE to paid accounts.
   */
  private shouldShowOnboarding(userMetadata: UserMetadata): boolean {
    if (this.isPaidAccountEligible(userMetadata)) {
      return true;
    }

    return (
      userMetadata.hasFreeOnboardingFeature &&
      !userMetadata.hasPaidOnboardingFeature
    );
  }

  /**
   * Checks if paid account is eligible for onboarding
   */
  private isPaidAccountEligible(userMetadata: UserMetadata): boolean {
    const { hasPaidOnboardingFeature, accountCreatedAt } = userMetadata;

    if (!hasPaidOnboardingFeature || !accountCreatedAt) {
      return false;
    }

    const releaseDate = new Date(
      environment.userFlow.paidAccountsOnboardingReleaseDate
    );
    const createdDate = new Date(accountCreatedAt);

    return createdDate > releaseDate;
  }

  /**
   * Sets the appropriate checklist IDs based on PFM onboarding features.
   *
   * TEMP WORKAROUND (CC-1147 / PSUP-5378): paid accounts carry both FEAT_ONBOARDING_FREE
   * (via PROD_BASIC_ACCESS) and FEAT_ONBOARDING_PAID (via PROD_PLUS_ACCESS), so a plain
   * "free first" check would start paid accounts on the free checklist. Paid takes priority:
   * eligible paid accounts get the paid checklists; the free checklist applies only to accounts
   * that do NOT carry the paid feature (paid accounts never fall back to free — kept in sync
   * with shouldShowOnboarding). Revert to the plain free/paid branching once the backend ships
   * the clean fix.
   */
  private setChecklistIds(userMetadata: UserMetadata): void {
    const { hasFreeOnboardingFeature, hasPaidOnboardingFeature } = userMetadata;

    if (this.isPaidAccountEligible(userMetadata)) {
      this.checklistIds = [
        environment.userFlow.paidAccountChecklistId,
        environment.userFlow.advancedPaidAccountChecklistId,
      ];
    } else if (hasFreeOnboardingFeature && !hasPaidOnboardingFeature) {
      this.checklistIds = [environment.userFlow.freeAccountChecklistId];
    } else {
      this.checklistIds = [];
    }
  }

  /**
   * Maps account state to UserFlow identity
   */
  private mapToUserFlowIdentity(userMetadata: UserMetadata): UserFlowIdentity {
    return {
      identityRef: userMetadata.profile.identityRef,
      email: userMetadata.profile.email,
      firstName: userMetadata.profile.given_name,
      lastName: userMetadata.profile.family_name,
      createdAt: userMetadata.createdAt,
      accountId: userMetadata.accountId,
      accountCreatedAt: userMetadata.accountCreatedAt,
      // TEMP WORKAROUND (CC-1147 / PSUP-5378): paid accounts also carry FEAT_ONBOARDING_FREE,
      // so keep isFreeAccount mutually exclusive with the paid flag — otherwise free-targeted
      // Userflow flows would also match paid accounts. Revert to the raw free flag once the
      // backend stops sending FEAT_ONBOARDING_FREE to paid accounts.
      isFreeAccount:
        userMetadata.hasFreeOnboardingFeature &&
        !userMetadata.hasPaidOnboardingFeature,
      isPaidAccount: userMetadata.hasPaidOnboardingFeature,
      // CC-1128: no PFM equivalent for isTransitionedToFreeAccount yet; defaults to false
      // TODO [CC-1200]: clarify with PO whether isTransitionedToFreeAccount is still needed in the new PFM model (CC-1144)
      isTransitionedToFreeAccount: false,
      metadata: [],
      locale_code: userMetadata.locale_code,
      customerSegment: userMetadata.company.customerSegment,
    };
  }

  /**
   * Sets up UserFlow with the given identity
   */
  private async setupUserFlow(userMetadata: UserMetadata): Promise<void> {
    const identity = this.mapToUserFlowIdentity(userMetadata);

    try {
      await this.userFlowService.init({
        apiKey: environment.userFlow.apiKey,
        user: identity,
        channel: {
          channelId: userMetadata.channelId,
        },
      });

      // Configure single-page navigation
      this.userFlowService.userflow.setCustomNavigate((url) => {
        this.router.navigate([url]);
      });

      this.userMetadata$.next(userMetadata);

      this.logService.debug(
        UserOnboardingInitService.TYPE,
        'UserFlow initialized',
        identity
      );
    } catch (error) {
      this.logError('UserFlow initialized failed', error);
    }
  }

  /**
   * Sets up channel association when UserFlow is ready
   */
  private setupChannelAssociation(): void {
    combineLatest([
      this.userFlowService.isReady$,
      this.channelsService.selectedChannel$,
      this.userMetadata$,
    ])
      .pipe(
        filter(([isReady, channel, user]) => isReady && !!channel && !!user),
        switchMap(([, channel, user]) =>
          this.userFlowAssociationService
            .setupChannel(
              user.accountId,
              channel as OrganizationalContainer<{ createdAt: Date }>
            )
            .pipe(map(() => channel.id))
        ),
        tap(async (channelId) => {
          try {
            // Set up channel permissions
            await this.userFlowPermissionsService.updateChannelPermissions(
              channelId
            );
          } catch (error) {
            this.logError(
              'Failed to update channel permissions in UserFlow',
              error
            );
          }
        })
      )
      .subscribe();
  }

  /**
   * Sets up account permissions association when UserFlow is ready
   */
  private updateUserPermissionsInUserflow(): void {
    this.userFlowService.isReady$
      .pipe(
        filter((isReady) => isReady),
        first(),
        tap(async () => {
          try {
            await this.userFlowPermissionsService.updateAccountPermissions();
          } catch (error) {
            this.logError(
              'Failed to update account permissions in UserFlow',
              error
            );
          }
        })
      )
      .subscribe();
  }

  /**
   * Updates the progress state based on onboarding result
   */
  private updateProgressState(result: OnboardingProgress | null): void {
    if (result) {
      this.ongoingOnboardingId = result.contentId;
      this.progress$.next(result.progress);
    } else {
      this.ongoingOnboardingId = undefined;
      this.progress$.next(UserOnboardingInitService.PROGRESS_COMPLETE);
    }
  }

  /**
   * Logs errors consistently
   */
  private logError(message: string, error?: unknown): void {
    const errorMessage = error
      ? `${message}: ${JSON.stringify(error)}`
      : message;
    this.logService.error(
      UserOnboardingInitService.TYPE,
      new Error(errorMessage)
    );
  }
}
